<template>
  <div
    class="button-base"
    :class="{
      'button-base__disabled': disabled,
      'button-base__large': size === 'large',
      'button-base__medium': size === 'medium',
      'button-base__medium-icon': size === 'medium-icon',
      'button-base__small': size === 'small',
      'button-base__small-icon': size === 'small-icon',
      'button-base__extra-small': size === 'extra-small',
      'button-base__extra-small-icon': size === 'extra-small-icon',
      'button-base__fill': size === 'fill'
    }"
    role="button"
    :tabindex="disabled ? -1 : tabIndex"
    @click="onClicked"
    @keydown.enter="onClicked"
    @keydown.space="(event: Event) => onClicked(event, true)"
  >
    <img
      v-if="iconLeft.length"
      alt="Left icon"
      :class="{ 'button-base__white-content': whiteContent }"
      :src="iconLeft"
    />
    <slot name="left" />
    <div v-if="buttonText.length" :class="['text', { 'text__white-content': whiteContent }]">
      {{ buttonText }}
    </div>
    <slot name="right" />
    <img
      v-if="iconRight.length"
      alt="Right icon"
      :class="{ 'button-base__white-content': whiteContent }"
      :src="iconRight"
    />
  </div>
</template>

<script lang="ts" setup>
const props = defineProps({
  buttonText: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  iconLeft: { type: String, default: '' },
  iconRight: { type: String, default: '' },
  size: { type: String, default: 'medium' },
  whiteContent: { type: Boolean, default: false },
  tabIndex: { type: Number, default: 0 }
})
const emit = defineEmits(['onClicked'])
function onClicked(event?: Event, preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  if (!props.disabled) emit('onClicked', event)
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.button-base {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  @include font-mixins.desktop-bold-paragraph;
  gap: variables.$gap-small;
  justify-content: center;
  padding: variables.$padding-medium variables.$padding-xlarge;
  width: fit-content;
  .text {
    &__white-content {
      color: variables.$color-pure-white;
    }
  }
  &__disabled {
    background: variables.$color-disabled;
    border: none;
    cursor: default;
  }
  &__fill {
    width: 100%;
    height: 100%;
    padding: 8px 16px;
    justify-content: flex-start;
    @include font-mixins.desktop-bold-label;
  }
  &__large {
    border-radius: variables.$border-radius-medium;
    @include font-mixins.desktop-bold-h5;
    height: variables.$button-large;
    min-width: variables.$button-large;
  }
  &__medium {
    border-radius: variables.$border-radius-medium;
    height: variables.$button-medium;
    min-width: variables.$button-medium;
  }
  &__medium-icon {
    border-radius: variables.$border-radius-medium;
    height: variables.$button-icon-medium;
    width: variables.$button-icon-medium;
  }
  &__small {
    border-radius: variables.$border-radius-small;
    @include font-mixins.desktop-bold-label;
    height: variables.$button-small;
    min-width: variables.$button-small;
  }
  &__small-icon {
    border-radius: variables.$border-radius-small;
    @include font-mixins.desktop-bold-label;
    height: variables.$button-small;
    width: variables.$button-small;
    padding: variables.$padding-medium;
  }
  &__extra-small {
    border-radius: variables.$border-radius-small;
    @include font-mixins.desktop-bold-tiny;
    height: variables.$button-xsmall;
    min-width: variables.$button-xsmall;
    padding: variables.$padding-medium;
  }
  &__extra-small-icon {
    border-radius: variables.$border-radius-small;
    @include font-mixins.desktop-bold-tiny;
    height: variables.$button-xsmall;
    width: variables.$button-xsmall;
    padding: variables.$padding-medium;
  }
  &__white-content {
    color: variables.$color-pure-white;
    filter: brightness(0) saturate(0) invert(100%);
  }
}
</style>
